// FILE: CompetitorsComparisonSection.tsx (FINAL VERSION with Mobile Scrolling)

import React from 'react';
import clsx from 'clsx';

export type ComparisonRow = {
  feature: string;
  us: string | boolean;
  altA?: string | boolean;
  altB?: string | boolean;
};

export type CompetitorsComparisonProps = {
  title?: string;
  subtitle?: string;
  labels: { us: string; altA?: string; altB?: string };
  rows: ComparisonRow[];
  disclaimer?: string;
  className?: string;
};

const cell = 'px-4 py-3 text-sm md:text-base border-b border-slate-200 align-top';
const th = 'px-4 py-3 text-left text-sm font-bold text-slate-900 border-b-2 border-slate-300 whitespace-nowrap'; // Added whitespace-nowrap
const yes = <span className="inline-flex px-2 py-0.5 rounded-lg bg-emerald-100 border-2 border-black text-xs font-bold">Yes</span>;
const no = <span className="inline-flex px-2 py-0.5 rounded-lg bg-slate-100 border-2 border-black text-xs font-bold">No</span>;

function render(val?: string | boolean) {
  if (val === true) return yes;
  if (val === false) return no;
  if (typeof val === 'string') return <span className="text-slate-800">{val}</span>;
  return <span className="text-slate-400">—</span>;
}

export const CompetitorsComparisonSection: React.FC<CompetitorsComparisonProps> = ({
  title,
  subtitle,
  labels,
  rows,
  disclaimer,
  className,
}) => {
  if (!rows || !rows.length) return null;

  const usColumnClass = 'bg-emerald-50/60';

  return (
    <section className={clsx('relative bg-white', className)}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        {(title || subtitle) && (
          <div className="mb-8 md:mb-10 text-center max-w-4xl mx-auto">
            {title && <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">{title}</h2>}
            {subtitle && <p className="mt-3 text-lg text-slate-700">{subtitle}</p>}
          </div>
        )}

        <div className="border-2 border-black rounded-2xl overflow-hidden bg-white">
          {/* --- START OF THE FIX: Add a scrollable wrapper --- */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]"> {/* Added min-width for better scrolling behavior */}
              <thead>
                <tr>
                  <th scope="col" className={th}>Feature</th>
                  <th scope="col" className={clsx(th, usColumnClass)}>{labels.us}</th>
                  {labels.altA ? <th scope="col" className={th}>{labels.altA}</th> : null}
                  {labels.altB ? <th scope="col" className={th}>{labels.altB}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <th scope="row" className={clsx(cell, 'font-bold text-slate-900 text-left')}>{r.feature}</th>
                    <td className={clsx(cell, usColumnClass)}>{render(r.us)}</td>
                    {labels.altA ? <td className={cell}>{render(r.altA)}</td> : null}
                    {labels.altB ? <td className={cell}>{render(r.altB)}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* --- END OF THE FIX --- */}
        </div>

        {disclaimer ? (
          <p className="mt-3 text-xs text-slate-500 text-center">{disclaimer}</p>
        ) : null}
      </div>
    </section>
  );
};

export default CompetitorsComparisonSection;